import { LEAD_WEBHOOK_URL } from '../config'
import type { LeadPayload } from '../types/lead'

export interface SubmitResult {
  ok: boolean
  error?: string
}

/**
 * Sends the lead payload to the configured CRM webhook and AWAITS the response.
 * Success is only reported on a successful HTTP response.
 *
 * NOTE: `mode: 'no-cors'` is deliberately NOT used — a fire-and-forget request that
 * silently drops leads is not acceptable. The webhook endpoint MUST return proper
 * CORS headers (Access-Control-Allow-Origin for miamicarqueen.com, and a 204 on OPTIONS).
 */
export async function submitLeadToCRM(payload: LeadPayload): Promise<SubmitResult> {
  if (!LEAD_WEBHOOK_URL) {
    // No endpoint configured: the lead was NOT delivered anywhere, so this must
    // never report success. The form shows its error + retry state instead.
    // The payload is logged so it can still be verified while wiring the webhook.
    // eslint-disable-next-line no-console
    console.error('[lead] LEAD_WEBHOOK_URL is empty — payload NOT sent:', payload)
    return { ok: false, error: 'NO_WEBHOOK' }
  }

  try {
    const response = await fetch(LEAD_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      // eslint-disable-next-line no-console
      console.error('[lead] webhook responded', response.status, payload)
      return { ok: false, error: `HTTP ${response.status}` }
    }

    return { ok: true }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[lead] webhook request failed', error, payload)
    return { ok: false, error: String(error) }
  }
}

/** RFC4122-ish v4 id used as the shared eventId for pixel + server deduplication. */
export function createEventId(): string {
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined
  if (cryptoObj && 'randomUUID' in cryptoObj) return cryptoObj.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}
